<template lang="pug">
  .b-task-tile-rename(v-if="rename")
    NameForm(
      field="Название задачи"
      error="Укажите название задачи"
      :value="task.name"
      @submit="updateTask('name', $event); rename = false"
      @abort="rename = false"
    )
  .b-task-tile(v-else :class="{ grey: task.isClosed }")
    ._checkbox
      slot(name="checkbox")
    ._head
      Link._title(:to="{ name: 'task-detail', params: { projectId: project.id, stageId: task.stage.id, taskId: task.id } }") {{ task.name.replaceAll(' ', '') ? task.name : "Без названия" }}
        template(v-if="notificationCount > 0") {{ '&nbsp;    ' }}
          NotificationCount(:count="notificationCount")
      TaskStatus._status(
        :value="task.status"
        :gray="task.isClosed"
        :changeable="access.canUpdateTask()"
        :editable="access.canEditProject()"
        :project="project"
        @change="updateTask('status', $event)"
        @edited="$store.commit('project/setStatuses', $event);"
      )
    ._priority
      BasePriority(:level="task.priority" :gray="task.isClosed" :editable="access.canUpdateTask(true)" @change="updateTask('priority', $event)")
    ._datetime(:class="dateFinishClass" @dragstart.stop.prevent draggable)
      div(v-if="task.dateFinish" @click="access.canUpdateTask(true) && $refs.dp.open()") {{ dateFinishStr }}
      div(v-else @click="access.canUpdateTask(true) && $refs.dp.open()") Без<br>срока
      BaseDatepicker._datepicker(type="datetime" @input="dateChanged" :value="dateFinish" hidden ref="dp" @dragstart.native.prevent)
    UserUniversalTile._author(:selected="task.author" :editable="access.canUpdateTask(true)" :items="$store.getters['project/members']" @change="updateTask('author', $event)" label="Нет постановщика" emptyLabel="Нет участников проекта")
    UserUniversalTile._manager(:selected="task.manager" :editable="access.canUpdateTask(true)" :items="$store.getters['project/members']" @change="updateTask('manager', $event)" label="Нет исполнителя" emptyLabel="Нет участников проекта")
    EllipsisMenu._ellipsis(:items="ellipsis")
</template>

<script>
import modelTaskList from '@/model/taskList';
import modelPopup from '@/model/popup';

import utils from '@/utils/common';
import Access from '@/utils/access';

import UserUniversalTile from '@/components/UserUniversalTile.vue';
import BasePriority from '@/components/BasePriority.vue';
import TaskStatus from '@/components/task/TaskStatus.vue';
import NotificationCount from '@/components/base/NotificationCount.vue';
import BaseDatepicker from '@/components/BaseDatepicker.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import NameForm from '@/components/base/NameForm.vue';
import TaskCopyPopup from '@/components/task/TaskCopyPopup.vue';

export default {
  name: 'TaskTile',
  components: { NameForm, UserUniversalTile, TaskStatus, BasePriority, BaseDatepicker, EllipsisMenu, NotificationCount }, // eslint-disable-line
  props: {
    task: Object,
    project: Object,
  },
  data() {
    return {
      rename: false,
    };
  },
  computed: {
    access() { return new Access({ ...this.task, project: this.project }, this.$store.state.auth.user); },
    dateFinish() { return this.task.dateFinish ? new Date(this.task.dateFinish) : null; },
    dateFinishStr() { return utils.convertDateToString(new Date(this.task.dateFinish), 'short', true, false); },
    dateFinishClass() {
      if (this.task.isClosed) return '';
      if (!this.dateFinish) return '';

      const now = new Date();
      if (this.dateFinish < now) {
        return 'outdated';
      }

      if (this.dateFinish - now < 3 * 60 * 60 * 1000) {
        return 'burning';
      }

      if (this.dateFinish - now < 24 * 60 * 60 * 1000) {
        return 'hot';
      }

      return '';
    },
    notificationCount() {
      const getter = this.$store.getters['notification/itemIdsByFilter'];
      return getter({ taskId: this.task.id }).length;
    },
    ellipsis() {
      if (!this.access.canUpdateTask(true)) return [];

      return [
        { text: 'Переименовать',        icon: 'edit',   handler: () => { this.rename = true; } }, // eslint-disable-line
        { text: 'Редактировать',        icon: 'edit',   route: { name: 'task-edit', params: { stageId: this.task.stage.id, taskId: this.task.id } } }, // eslint-disable-line
        this.task.isClosed
          ? { text: 'Открыть',              icon: 'close',   handler: () => { this.updateTask('isClosed', false); } } // eslint-disable-line
          : { text: 'Закрыть',              icon: 'close',   handler: () => { this.updateTask('isClosed', true); } }, // eslint-disable-line
        { text: 'Копировать',           icon: 'edit',   handler: () => { this.copyTask('task'); } }, // eslint-disable-line
        { text: 'Копировать чеклист',   icon: 'edit',   handler: () => { this.copyTask('checklist'); } }, // eslint-disable-line
        { text: 'Удалить',              icon: 'trash',  handler: () => { modelTaskList.remove(this.task) } }, // eslint-disable-line
      ];
    },
  },
  methods: {
    dateChanged(value) {
      this.updateTask('dateFinish', value);
    },
    updateTask(field, value) {
      const { id } = this.task;
      modelTaskList.update({ id, field, value });
    },
    copyTask(mode) {
      modelPopup.add({
        args: [mode, this.task.id],
        component: TaskCopyPopup,
      });
    },
  },
};
</script>

<style scoped lang="scss">

.task-tile-rename {
  background: rgba(235, 239, 242, 0.5);
  border-radius: 6px;
  padding: 12px 24px;
}

.task-tile {
  background: rgba(235, 239, 242, 0.5);
  border-radius: 6px;
  padding: 12px 12px;
  display: grid;
  gap: 15px;
  grid-template-columns: 20px 1fr 35px 60px 140px 140px 24px;
  align-items: center;

  &.grey {
    background: #e6ecf2;
  }

  &.grey &__head a {
    color: #c5c5c5;
    text-decoration: underline;
    text-underline-offset: -.4em;
    text-decoration-skip-ink: none;
    text-decoration-thickness: 2px;
  }

  &__head {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  &__title {
    transition: color 0.3s;
    &:hover {
      color: var(--color-hover);
    }
  }

  &__status {
    //margin-bottom: 3px;
    width: fit-content;
  }

  &__priority {
    width: fit-content;
  }

  &__datetime {
    position: relative;
    text-align: center;
    font-size: 12px;
    line-height: 15px;
    justify-self: center;
    letter-spacing: 0.05em;
    color: var(--color-middle-gray);
    cursor: pointer;

    &.outdated {
      color: #EA4335;
    }

    &.burning {
      color: #FF8D4C;
    }

    &.hot {
      color: #F9D208;
    }
  }

  &__datepicker {
    position: absolute;
    cursor: default;
  }

  &__manager {
    position: relative;
    //background: green;

    &::before {
      position: absolute;
      content: '';
      display: block;
      top: 50%;
      left: -20px;
      translate: 0 -50%;
      background: url(../../assets/img/svg/angle-down.svg) center center no-repeat;
      height: 5px;
      width: 8px;
      rotate: -90deg;
    }
  }

  &__manager {
    &.grey {
      filter: grayscale(1);
      opacity: .7;
    }

    &::v-deep .name {
      font-size: 13px;
    }
  }

  @media (max-width: 850px) {
    background: #fff;
    padding: 15px 0;
    border-bottom: 1px solid #D1D9F0;
    border-radius: 0;

    grid-template-columns: 20px min-content 1fr 35px 60px 50px 30px;
    grid-template-areas:
      'checkbox title title title title title menu'
      'status status priority datetime datetime author manager'
    ;

    &__checkbox { grid-area: checkbox; }
    &__title { grid-area: title; }
    &__ellipsis { grid-area: menu; }
    &__datetime { grid-area: datetime; }
    &__priority { grid-area: priority; }
    &__status { grid-area: status; }
    &__manager { grid-area: manager; }
    &__author { grid-area: author; }

    &__head {
      display: contents;
    }

    &__manager:before {
      left: -22px;
    }

    &__ellipsis {
      justify-self: end;
    }

    &__manager,
    &__author {
      ::v-deep  {

        .user-tile .user-tile__image, .avatar {
          width: 30px;
          height: 30px;
          border-radius: 12px;
        }

        .user-tile .body, .name {
          display: none;
        }
      }
    }
  }

  @media (max-width: 500px) {
    background: #fff;
    padding: 11px 0;
    border-bottom: 1px solid #D1D9F0;
    border-radius: 0;
    gap: 8px;

    grid-template-columns: 20px min-content 1fr 50px 30px;
    grid-template-areas:
      'checkbox title title datetime menu'
      'status status priority author manager'
    ;

    &__manager:before {
      left: -17px;
    }
  }
}
</style>
